import { DependencyList, useContext, useEffect } from 'react'
import { ButtonContext, ButtonState } from '@/app/_providers/ButtonProvider/ButtonProvider'

type Props = {
  /* The new button state to be set */
  newButtonState: Partial<ButtonState>
  /* The condition required to update the button state */
  updateStateCondition: boolean
  /* The dependency array for the useEffect hook */
  dependencyArray: DependencyList
}

/**
 A custom React hook that updates the button state in the ButtonContext based on the given condition.
 @function
 @name useUpdateButtonState
 @param {Object} props - The hook's input properties.
 @param {Partial<ButtonState>} props.newButtonState - The new button state to be applied when the condition is met.
 @param {boolean} props.updateStateCondition - A condition to be checked before updating the button state.
 @param {DependencyList} props.dependencyArray - An array of dependencies for the useEffect hook.
 @returns {void}
 @example
 useUpdateButtonState({
    newButtonState: {
      disabled: btnDisabled,
      text: TXT.buttons.purchase,
      action: submitForm,
      ...(type === PaymentMethodType.card ? { paymentRequest: undefined } : {}),
    },
    updateStateCondition: step === STEPS.Checkout,
    dependencyArray: [step, btnDisabled, paymentMethod],
    });
 */
export const useUpdateButtonState = (props: Props): void => {
  const {
    newButtonState,
    updateStateCondition,
    dependencyArray,
  } = props

  const { updateButtonState } = useContext(ButtonContext)

  useEffect(() => {
    if (updateStateCondition) updateButtonState(newButtonState)
  }, [...dependencyArray])
}
